import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { Badge } from '@ctw/shared/components/badge';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { getNoteDisplay } from '@ctw/shared/content/resource/notes';
import { NoteSummary } from '@ctw/shared/content/resource/notes-entry';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { recursivelyTransposeTables } from '@ctw/shared/utils/transpose-table';
import { useState } from 'react';

export interface RelatedDocumentsListItem {
  preferred?: boolean;
  document: DocumentModel;
}

export interface RelatedDocumentsListProps {
  isLoading: boolean;
  items: Array<RelatedDocumentsListItem>;
}

export const RelatedDocumentsList = ({ isLoading, items }: RelatedDocumentsListProps) => {
  if (isLoading) {
    return (
      <div className={tw`flex w-full justify-center`}>
        <LoadingSpinner className={tw`text-content-icon`} message="Loading clinical notes" />
      </div>
    );
  }
  if (items.length > 0) {
    return (
      <div className={tw`space-y-4`}>
        <div className={tw`font-semibold text-lg`}>Notes</div>
        {items.map((item) => (
          <div
            key={item.document.id}
            className={tw`flex flex-col gap-2 divide-y divide-divider-main rounded-lg border bg-white p-3 text-left`}
          >
            <div className={tw`flex items-center justify-between gap-2`}>
              <div className={tw`flex min-w-0 flex-1 items-center gap-2`}>
                <span
                  className={tw`flex flex-wrap items-center justify-between gap-1 whitespace-normal break-words font-medium text-background-inverse text-base`}
                >
                  {item.document.title}
                  {item.preferred && <Badge variant="meta" content="Notes" />}
                </span>
              </div>
              <div className={tw`flex shrink-0 items-center justify-center`}>
                <ResponsiveSourceDocumentLink
                  model={item.document}
                  binaryId={item.document.binaryId}
                />
              </div>
            </div>
            {item.document.sectionDocuments && item.document.sectionDocuments.length > 0 && (
              <div className={tw`divide-y divide-divider-main`}>
                {item.document.sectionDocuments.map((sectionDocument) => (
                  <div
                    key={sectionDocument.id}
                    className={tw`last:!border-b border-divider-main py-1`}
                  >
                    <RelatedDocumentSection section={sectionDocument} key={sectionDocument.id} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return <></>;
};

interface RelatedDocumentSectionProps {
  section: DocumentModel;
}

const RelatedDocumentSection = ({ section }: RelatedDocumentSectionProps) => {
  const [isDetailShown, setIsDetailShown] = useState(false);

  return (
    <div
      key={section.id}
      className={twx(
        isDetailShown && 'divide-y rounded-lg border-y-border-main bg-background-hover px-4',
      )}
    >
      <NoteSummary
        summary={
          <>
            {section.title && <div>{section.title}</div>}
            {!section.title && <div className={tw`text-content-icon`}>Unknown</div>}
          </>
        }
        isDetailShown={isDetailShown}
        setIsDetailShown={setIsDetailShown}
        className={twx(
          'section-note-expandable',
          isDetailShown ? 'rounded-t-lg bg-background-hover' : 'rounded-lg px-4',
        )}
      />
      {isDetailShown && (
        <ContentCard variant="focal" title="Details">
          <DefinitionList
            items={[
              {
                label: 'Note',
                value: recursivelyTransposeTables(getNoteDisplay(section.displayContent), 0),
              },
            ]}
          />
        </ContentCard>
      )}
    </div>
  );
};
